/**
 * @file loggingService.js
 * @description This file contains the code for logging events to the server.
 */

import { loggingApiHttp } from './httpService';
import qs from 'qs';

const getTenantId = () => +window.localStorage.getItem('tenantId');

const appendIfDefined = (payload, key, value) => {
    if (value !== null && value !== undefined) {
        payload[key] = value;
    }
};

const normalizeEventFields = (
    subjectDataTypeOrFields,
    subjectId,
    subjectName,
    contextDataTypeId,
    contextId,
    contextName,
) => {
    if (
        subjectDataTypeOrFields &&
        typeof subjectDataTypeOrFields === 'object' &&
        !Array.isArray(subjectDataTypeOrFields)
    ) {
        return subjectDataTypeOrFields;
    }

    return {
        subjectDataTypeId: subjectDataTypeOrFields,
        subjectId,
        subjectName,
        contextDataTypeId,
        contextId,
        contextName,
    };
};

export const logEvent = async (
    eventTypeId,
    message,
    subjectDataTypeOrFields = null,
    subjectId = null,
    subjectName = null,
    contextDataTypeId = null,
    contextId = null,
    contextName = null,
) => {
    try {
        const eventFields = normalizeEventFields(
            subjectDataTypeOrFields,
            subjectId,
            subjectName,
            contextDataTypeId,
            contextId,
            contextName,
        );

        const payload = {
            tenantId: getTenantId(),
            eventTypeId,
            timestamp: new Date().toISOString(),
            message,
        };

        appendIfDefined(
            payload,
            'subjectDataTypeId',
            eventFields.subjectDataTypeId,
        );
        appendIfDefined(payload, 'subjectId', eventFields.subjectId);
        appendIfDefined(payload, 'subjectName', eventFields.subjectName);
        appendIfDefined(
            payload,
            'contextDataTypeId',
            eventFields.contextDataTypeId,
        );
        appendIfDefined(payload, 'contextId', eventFields.contextId);
        appendIfDefined(payload, 'contextName', eventFields.contextName);

        const response = await loggingApiHttp.post(`v2/logging/event`, payload);

        return response.data;
    } catch (error) {
        console.error('Failed to log event:', error);
    }
};

export const logAnonymousEvent = async (
    eventTypeId,
    message,
    companyCode,
    username,
) => {
    try {
        const payload = {
            companyCode: companyCode,
            username: username,
            eventTypeId: eventTypeId,
            timestamp: new Date().toISOString(),
            message: message,
        };

        const response = await loggingApiHttp.post(
            `v2/logging/event/anonymous`,
            payload,
        );

        return response.data;
    } catch (error) {
        console.error('Failed to log anonymous event:', error);
    }
};

export const getEventTypes = async () => {
    try {
        const response = await loggingApiHttp.get(`v2/logging/event/type`);
        return response.data;
    } catch (error) {
        console.error('Failed to get event types:', error);
    }
};

export const getBrowsers = async () => {
    try {
        const response = await loggingApiHttp.get(
            `v2/logging/event/browser?tenantId=${getTenantId()}`,
        );
        return response.data;
    } catch (error) {
        console.error('Failed to get browsers:', error);
    }
};

export const getDevices = async () => {
    try {
        const response = await loggingApiHttp.get(
            `v2/logging/event/device?tenantId=${getTenantId()}`,
        );
        return response.data;
    } catch (error) {
        console.error('Failed to get devices:', error);
    }
};

export const getEventHistory = async eventId => {
    try {
        const response = await loggingApiHttp.get(
            `v2/logging/event/${eventId}/history`,
        );

        return response.data;
    } catch (error) {
        console.error('Failed to get event history:', error);
    }
};

export const getLog = async searchCriteria => {
    try {
        const queryString = qs.stringify(
            { ...searchCriteria },
            { arrayFormat: 'repeat' },
        );

        const response = await loggingApiHttp.get(
            `v2/logging/event?${queryString}&tenantId=${getTenantId()}`,
        );

        return response.data;
    } catch (error) {
        console.error('Failed to log event:', error);
    }
};
